// The fake api's printing and cash-drawer routes (F-OPS-08, F-OPS-09, F-FNB-05; spec/13 §11), in the contract's
// shapes — enough for Settings → Printers and hardware, the kitchen board's Print, the order sheet's Print receipt
// and the Shifts screen's Open drawer to be rendered against the fake in `fake-server.ts`:
//
//   GET    …/venues/{v}/printers                   listPrinters        settings:hardware
//   POST   …/venues/{v}/printers                   createPrinter       settings:hardware
//   PATCH  …/venues/{v}/printers/{p}               updatePrinter       settings:hardware
//   DELETE …/venues/{v}/printers/{p}               archivePrinter      settings:hardware
//   POST   …/venues/{v}/printers/{p}/test          testPrinter         settings:hardware
//   GET    …/venues/{v}/print-jobs                 listPrintJobs       settings:hardware
//   POST   …/venues/{v}/sales/{s}/receipt          printSaleReceipt    orders:read
//   POST   …/venues/{v}/kitchen/tickets/{t}/print  printKitchenTicket  kitchen:read
//   POST   …/venues/{v}/drawer/open                openDrawer          shifts:drawer_open
//
// The venue starts with one desk printer on the Counter PC (receipts, the cash drawer on pin 2) unless a test
// seeds its own. A cloud printer's polling URL is answered only by the create and a key rotation, as the server
// does. Where a kitchen ticket or a receipt goes is the test's choice (`kitchenTarget`, `receiptTarget`); every
// print is a new job.

import type { KitchenTicket, Printer, PrintDocument, PrintJob, PrintTarget, Problem } from '@clavian/api-client';

import type { FakeResponse } from './fake-server';

const VENUE_PATH = '/v1/organizations/org_01k5znw4hmfay8vvr14d2pf2db/venues/ven_01k5znw4hmfay8vvr14d2pf2dc';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const NOW = '2026-09-22T09:00:00.000Z';
const EXPIRES = '2026-09-22T09:05:00.000Z';

/** The Clavian Counter PC the fake's `/stations` lists with `counterStation` (role `counter`). */
export const COUNTER_STATION_ID = 'stn_01k5znw4hmfay8vvr14d2pf3c9';
export const DESK_PRINTER_ID = 'prn_01k5znw4hmfay8vvr14d2pf4a1';

export function printerFixture(overrides: Partial<Printer> = {}): Printer {
  return {
    id: DESK_PRINTER_ID,
    venueId: VENUE_ID,
    name: 'Counter receipts',
    kind: 'desk',
    purposes: ['receipt'],
    paperWidth: 80,
    desk: { stationId: COUNTER_STATION_ID, transport: 'tcp', host: '192.168.1.50', port: 9100, queueName: null },
    cloud: null,
    cashDrawer: true,
    drawerPin: 2,
    fallbackPrinterId: null,
    status: 'online',
    lastSeenAt: NOW,
    lastError: null,
    createdAt: NOW,
    updatedAt: NOW,
    ...overrides,
  };
}

export interface PrintingOptions {
  /** The venue's printers at the start; one desk receipt printer with a cash drawer by default. */
  readonly printers?: readonly Printer[];
  /** Where `printKitchenTicket` routes a ticket. `browser` by default. */
  readonly kitchenTarget?: PrintTarget;
  /** Where `printSaleReceipt` routes a receipt. `browser` by default. */
  readonly receiptTarget?: PrintTarget;
}

export interface PrintingHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly can: (permission: string) => boolean;
  /** The kitchen board's tickets (the commerce fake's), to print one. */
  readonly tickets: () => readonly KitchenTicket[];
}

const ROUTE = {
  printers: new RegExp(`^${VENUE_PATH}/printers$`),
  printer: new RegExp(`^${VENUE_PATH}/printers/(prn_[0-9a-z]+)$`),
  test: new RegExp(`^${VENUE_PATH}/printers/(prn_[0-9a-z]+)/test$`),
  jobs: new RegExp(`^${VENUE_PATH}/print-jobs$`),
  receipt: new RegExp(`^${VENUE_PATH}/sales/(sal_[0-9a-z]+)/receipt$`),
  kitchen: new RegExp(`^${VENUE_PATH}/kitchen/tickets/([^/]+)/print$`),
  drawer: new RegExp(`^${VENUE_PATH}/drawer/open$`),
};

export function createFakePrinting(helpers: PrintingHelpers, options: PrintingOptions = {}) {
  const { json, problem, can } = helpers;
  let sequence = 0;
  const fresh = (prefix: string) => `${prefix}_01k5znw4hmfay8vvr14d2pf${String(++sequence).padStart(3, '0')}`;
  const state = {
    printers: [...(options.printers ?? [printerFixture()])] as Printer[],
    jobs: [] as PrintJob[],
    kitchenTarget: options.kitchenTarget ?? ('browser' as PrintTarget),
    receiptTarget: options.receiptTarget ?? ('browser' as PrintTarget),
    /** What each create, edit and drawer kick asked for, for tests to read back. */
    bodies: [] as { readonly route: string; readonly body: unknown }[],
  };
  const forbidden = () => problem(403, 'forbidden', 'Not allowed');
  // What a read answers: never a cloud printer's polling URL (the key is stored hashed).
  const listed = (printer: Printer): Printer => (printer.cloud === null ? printer : { ...printer, cloud: { ...printer.cloud, pollUrl: null } });

  function job(input: { printerId: string | null; contentType: PrintJob['contentType']; target: PrintTarget; document: PrintDocument; purpose: string | null }): PrintJob {
    const made: PrintJob = {
      id: fresh('pjb'),
      venueId: VENUE_ID,
      printerId: input.printerId,
      purpose: input.purpose,
      contentType: input.contentType,
      target: input.target,
      status: input.target === 'browser' ? 'printed' : input.target === 'display' ? 'failed' : 'queued',
      document: input.document,
      deviceStationId: input.target === 'device' ? COUNTER_STATION_ID : null,
      commandId: null,
      reprintOf: null,
      error: input.target === 'display' ? 'The kitchen printer is offline.' : null,
      createdAt: NOW,
      sentAt: null,
      completedAt: input.target === 'browser' ? NOW : null,
      expiresAt: EXPIRES,
    };
    state.jobs.unshift(made);
    return made;
  }

  function handle(method: string, pathname: string, _search: URLSearchParams, input: unknown): FakeResponse | undefined {
    if (ROUTE.printers.test(pathname)) {
      if (!can('settings:hardware')) return forbidden();
      if (method === 'GET') return json(200, { printers: state.printers.map(listed) });
      if (method === 'POST') {
        const body = input as Record<string, unknown>;
        state.bodies.push({ route: 'create', body });
        const kind = body['kind'] as Printer['kind'];
        const id = fresh('prn');
        const desk = body['desk'] as { stationId: string; transport: 'tcp' | 'spooler'; host?: string; port?: number; queueName?: string } | undefined;
        const created = printerFixture({
          id,
          name: String(body['name']),
          kind,
          purposes: (body['purposes'] as Printer['purposes']) ?? [],
          paperWidth: (body['paperWidth'] as number) ?? 80,
          desk: kind === 'desk' && desk !== undefined ? { stationId: desk.stationId, transport: desk.transport, host: desk.host ?? null, port: desk.port ?? null, queueName: desk.queueName ?? null } : null,
          cloud: kind === 'cloud' ? { protocol: (body['cloudProtocol'] as 'star_cloudprnt' | 'epson_sdp') ?? 'star_cloudprnt', pollUrl: null } : null,
          cashDrawer: kind === 'desk' && body['cashDrawer'] === true,
          drawerPin: (body['drawerPin'] as number) ?? 2,
          fallbackPrinterId: (body['fallbackPrinterId'] as string | null | undefined) ?? null,
          status: 'unknown',
          lastSeenAt: null,
        });
        state.printers.push(created);
        const answer = kind === 'cloud' ? { ...created, cloud: { protocol: created.cloud?.protocol ?? 'star_cloudprnt', pollUrl: `https://api.clavian.test/v1/cloud-print/star/org/ven/${id}/${'k'.repeat(43)}` } } : created;
        return json(201, answer);
      }
    }
    const one = ROUTE.printer.exec(pathname);
    if (one !== null && (method === 'PATCH' || method === 'DELETE')) {
      if (!can('settings:hardware')) return forbidden();
      const found = state.printers.find((printer) => printer.id === one[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such printer at this venue.');
      if (method === 'DELETE') {
        state.printers = state.printers.filter((printer) => printer.id !== found.id).map((printer) => (printer.fallbackPrinterId === found.id ? { ...printer, fallbackPrinterId: null } : printer));
        return { status: 204, headers: {}, body: '' };
      }
      const body = input as Record<string, unknown>;
      state.bodies.push({ route: 'update', body });
      const { rotateCloudKey, desk, ...rest } = body as { rotateCloudKey?: boolean; desk?: { stationId: string; transport: 'tcp' | 'spooler'; host?: string; port?: number; queueName?: string } };
      const updated: Printer = {
        ...found,
        ...(rest as Partial<Printer>),
        ...(desk === undefined ? {} : { desk: { stationId: desk.stationId, transport: desk.transport, host: desk.host ?? null, port: desk.port ?? null, queueName: desk.queueName ?? null } }),
      };
      state.printers = state.printers.map((printer) => (printer.id === found.id ? updated : printer));
      if (rotateCloudKey === true && updated.cloud !== null) {
        return json(200, { ...updated, cloud: { ...updated.cloud, pollUrl: `https://api.clavian.test/v1/cloud-print/star/org/ven/${found.id}/${'r'.repeat(43)}` } });
      }
      return json(200, listed(updated));
    }
    const test = ROUTE.test.exec(pathname);
    if (test !== null && method === 'POST') {
      if (!can('settings:hardware')) return forbidden();
      const found = state.printers.find((printer) => printer.id === test[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such printer at this venue.');
      const target: PrintTarget = found.kind === 'browser' ? 'browser' : found.kind === 'cloud' ? 'cloud' : 'device';
      const document: PrintDocument = {
        paperWidth: found.paperWidth,
        cut: true,
        lines: [
          { kind: 'text', text: 'Test page', align: 'center', size: 'tall', bold: true },
          { kind: 'text', text: found.name, align: 'center' },
          { kind: 'rule' },
        ],
      };
      return json(202, job({ printerId: found.id, contentType: 'test', target, document, purpose: null }));
    }
    if (ROUTE.jobs.test(pathname) && method === 'GET') {
      if (!can('settings:hardware')) return forbidden();
      return json(200, { jobs: state.jobs });
    }
    const receipt = ROUTE.receipt.exec(pathname);
    if (receipt !== null && method === 'POST') {
      if (!can('orders:read')) return forbidden();
      const printer = state.printers.find((one) => one.purposes.includes('receipt'));
      const target = state.receiptTarget;
      const document: PrintDocument = {
        paperWidth: 80,
        cut: true,
        lines: [
          { kind: 'text', text: 'Playground Central', align: 'center', bold: true },
          { kind: 'rule' },
          { kind: 'item', text: 'Receipt line', quantity: 1, amount: { amount: 30_000, currency: 'INR' } },
          { kind: 'amount', text: 'Total', amount: { amount: 30_000, currency: 'INR' }, bold: true },
        ],
      };
      const made = job({ printerId: target === 'browser' ? null : (printer?.id ?? null), contentType: 'receipt', target, document, purpose: 'receipt' });
      return json(202, { job: made, target });
    }
    const kitchen = ROUTE.kitchen.exec(pathname);
    if (kitchen !== null && method === 'POST') {
      if (!can('kitchen:read')) return forbidden();
      const ticket = helpers.tickets().find((one) => one.id === kitchen[1]);
      if (ticket === undefined) return problem(404, 'not_found', 'No such kitchen ticket at this venue.');
      const target = state.kitchenTarget;
      const printer = state.printers.find((one) => one.purposes.includes('kitchen'));
      const document: PrintDocument = {
        paperWidth: 80,
        cut: true,
        lines: [
          { kind: 'text', text: ticket.orderNumber === null ? 'Order' : `#${String(ticket.orderNumber).padStart(4, '0')}`, size: 'large' },
          { kind: 'rule' },
          ...ticket.items.map((item) => ({ kind: 'item' as const, text: item.name, quantity: item.quantity, notes: [...item.modifiers, ...(item.note === null ? [] : [item.note])] })),
        ],
      };
      const made = job({ printerId: target === 'device' || target === 'cloud' ? (printer?.id ?? null) : null, contentType: 'kitchen_ticket', target, document, purpose: ticket.area });
      return json(200, { ticket, target, printedAt: NOW, job: made });
    }
    if (ROUTE.drawer.test(pathname) && method === 'POST') {
      if (!can('shifts:drawer_open')) return forbidden();
      state.bodies.push({ route: 'drawer', body: input });
      const printer = state.printers.find((one) => one.kind === 'desk' && one.cashDrawer);
      if (printer === undefined) return problem(409, 'conflict', 'No desk printer at this venue has a cash drawer.');
      return json(202, { commandId: fresh('cmd'), status: 'queued', printerId: printer.id, shiftId: null, createdAt: NOW, expiresAt: '2026-09-22T09:00:30.000Z' });
    }
    return undefined;
  }

  return { state, handle };
}

export type FakePrinting = ReturnType<typeof createFakePrinting>;
